import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Smartphone } from "lucide-react";
import { openApp, scrollToId } from "./Navbar";
import { VoiceScreen } from "./phone/screens";

const lines = ["Your life.", "Organized.", "Just ask."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="hero" ref={ref} className="grain relative overflow-hidden pt-16" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0">
        <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#E8F3EE] blur-3xl" />
        <div className="absolute -left-32 top-1/2 h-[380px] w-[380px] rounded-full bg-lime-100/70 blur-3xl" />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8 lg:px-8 lg:pb-28 lg:pt-20">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-1.5"
            data-testid="hero-badge"
          >
            <Smartphone size={13} className="text-emerald-700" />
            <span className="text-xs font-semibold text-emerald-900">A real mobile app — for Android & iOS</span>
          </motion.div>

          <h1 className="font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                  {i === 2 ? (
                    <>Just ask <span className="font-serif-accent italic text-[#2E7D5B]">GoDiGo</span>.</>
                  ) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
            data-testid="hero-subheadline"
          >
            GoDiGo is your personal and professional assistant. Speak or type in plain language — it turns your words into tasks, reminders, appointments and calendar events, and keeps everything on track.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-get-godigo-button" onClick={openApp} className="btn-primary px-7 py-3.5 text-base">
              Get GoDiGo <ArrowRight size={17} />
            </button>
            <button data-testid="hero-how-it-works-button" onClick={() => scrollToId("how-it-works")} className="btn-secondary px-7 py-3.5 text-base">
              <Play size={15} /> See How It Works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex items-center gap-6 text-sm text-slate-500"
          >
            <span className="flex items-center gap-2"><span className="relative inline-block h-2 w-2 rounded-full bg-emerald-500 ping-dot" /> Voice-first</span>
            <span>Gmail · Calendar · Contacts</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: phoneY }}
          className="flex justify-center lg:justify-end"
          data-testid="hero-phone-mockup"
        >
          <div className="soft-float">
            <VoiceScreen />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
